import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import DnsRoundedIcon from '@mui/icons-material/DnsRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import { alpha, Box, Grid2 as Grid, Stack, Typography, useTheme } from '@mui/material';

export type ModernDashboardSectionKind = 'overview' | 'configuration' | 'access' | 'sap' | 'notes';

const sectionConfig: Record<ModernDashboardSectionKind, { icon: ReactNode; titleId: string; descriptionId: string }> = {
  overview: {
    icon: <ApartmentRoundedIcon />,
    titleId: 'dashboard.modern.sections.overview.title',
    descriptionId: 'dashboard.modern.sections.overview.description',
  },
  configuration: {
    icon: <TuneRoundedIcon />,
    titleId: 'dashboard.modern.sections.configuration.title',
    descriptionId: 'dashboard.modern.sections.configuration.description',
  },
  access: {
    icon: <DnsRoundedIcon />,
    titleId: 'dashboard.modern.sections.access.title',
    descriptionId: 'dashboard.modern.sections.access.description',
  },
  sap: {
    icon: <LanRoundedIcon />,
    titleId: 'dashboard.modern.sections.sap.title',
    descriptionId: 'dashboard.modern.sections.sap.description',
  },
  notes: {
    icon: <NotesRoundedIcon />,
    titleId: 'dashboard.modern.sections.notes.title',
    descriptionId: 'dashboard.modern.sections.notes.description',
  },
};

const ModernDashboardSection = ({ kind }: { kind: ModernDashboardSectionKind }) => {
  const theme = useTheme();
  const section = sectionConfig[kind];

  return (
    <Grid size={{ xs: 12 }} className="dashboard-section-heading">
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          aria-hidden="true"
          sx={{
            width: 38,
            height: 38,
            display: 'grid',
            flexShrink: 0,
            placeItems: 'center',
            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.09),
            '& .MuiSvgIcon-root': { fontSize: 21 },
          }}>
          {section.icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            <FormattedMessage id={section.titleId} />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage id={section.descriptionId} />
          </Typography>
        </Box>
      </Stack>
    </Grid>
  );
};

export default ModernDashboardSection;
